/**
 * No AI Foundry requests for a person whose AI Foundry connection is off (sandbox-first, docs/SANDBOX_FIRST.md).
 *
 * The Catalog's AI items (`{bffUrl}/api/catalog/ai-*`: tools, playbooks, specs, agents, prompts, skills, models, MCP
 * servers) and the playbook auto-pick (`/api/mia-platform/ai-playbooks/select`) are AI Foundry's. Many places ask for
 * them, through the authorized fetch or the global one, so index.jsx puts this gate in front of both: while the
 * answer is not in it waits for the person's plan (`loadPlanFeatures`), then, with AI Foundry off, answers here
 * without touching the network:
 * - a list: `{ success: true, data: { items: [], total: 0 } }`, the BFF's own empty answer, so every caller just
 *   shows nothing;
 * - the playbook auto-pick: nothing selected, so the caller keeps Flow's default playbook;
 * - anything else (an item's detail): `{ success: false, error }`, status 200 so no caller logs a failed request.
 * With AI Foundry on, unknown, or without sandbox-first, every request goes through untouched.
 */
import config from '../config';
import { aiFoundryInUse, loadPlanFeatures } from '../hooks/usePlanFeatures';

export const NEEDS_AI_FOUNDRY = 'Needs the AI Foundry connection';

const CATALOG_AI = /\/api\/catalog\/ai-[a-z-]+(\/[^?#]*)?(?:[?#]|$)/;
const PLAYBOOK_SELECT = /\/api\/mia-platform\/ai-playbooks\/select(?:[?#]|$)/;

function requestUrl(input) {
  if (typeof input === 'string') { return input; }
  if (input instanceof URL) { return input.href; }
  return input?.url || '';
}

/** What an AI Foundry request is: 'list', 'detail', 'select', or null for any other request. */
export function aiFoundryRequestKind(input) {
  const url = requestUrl(input);
  if (PLAYBOOK_SELECT.test(url)) { return 'select'; }
  const match = CATALOG_AI.exec(url);
  if (!match) { return null; }
  return match[1] && match[1] !== '/' ? 'detail' : 'list';
}

function json(body) {
  return new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json' } });
}

/** The answer given in place of an AI Foundry request while the connection is off. */
export function offAnswer(kind) {
  if (kind === 'list') { return json({ success: true, data: { items: [], total: 0 }, aiFoundry: 'off' }); }
  if (kind === 'select') { return json({ success: true, selected: null, confidence: 0, reasoning: NEEDS_AI_FOUNDRY }); }
  return json({ success: false, error: NEEDS_AI_FOUNDRY, data: null });
}

/** `fetchImpl`, minus the AI Foundry requests of a person whose connection is off. */
export function gateAiFoundry(fetchImpl) {
  return async(input, init) => {
    const kind = config.sandboxFirst ? aiFoundryRequestKind(input) : null;
    if (kind) {
      await loadPlanFeatures();
      if (!aiFoundryInUse()) { return offAnswer(kind); }
    }
    return fetchImpl(input, init);
  };
}
